import { useCallback, useEffect, useMemo, useState } from 'react'
import { Menu } from '@highlight/ui'
import { Menu as BaseMenu } from '@base-ui/react/menu'
import { Check } from 'lucide-react'
import type { StagedActionEditTag, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useEditOptions } from './edit-options-context'
import { ActionTagBadgeCollection } from './badge-collection'
import { Icon } from './badge'

const MAX_VISIBLE_HEIGHT = 256

interface EditableSingleSelectProps {
  keyName: string
  tags: Array<StagedActionView['tags'][number]>
  editTag: StagedActionEditTag
}

export function EditableSingleSelect({ keyName, tags, editTag }: EditableSingleSelectProps) {
  const { onUpdate, isUpdating } = useEditOptions()
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null)

  const tagsFingerprint = tags.map((t) => t.value).join('\0')
  useEffect(() => {
    setOptimisticValue(null)
  }, [tagsFingerprint])

  const displayTags = useMemo(() => {
    if (optimisticValue === null) return tags
    const opt = editTag.options.find((o) => o.value === optimisticValue)
    if (!opt) return tags
    const groupKey = tags[0]?.alwaysShowKey
    return [
      {
        key: keyName,
        value: opt.name,
        icon: opt.icon,
        ...(groupKey ? { alwaysShowKey: groupKey } : {}),
      },
    ]
  }, [optimisticValue, tags, editTag, keyName])

  const currentValue = useMemo(() => {
    if (optimisticValue !== null) return optimisticValue
    const tag = tags[0]
    if (!tag) return undefined
    return editTag.options.find((o) => o.name === tag.value)?.value
  }, [tags, editTag, optimisticValue])

  const handleValueChange = useCallback(
    (value: string) => {
      if (isUpdating) return
      setOptimisticValue(value)
      const tagEdits: Record<string, Array<string>> = { [keyName]: [value] }
      if (editTag.constraints?.exclusive) {
        for (const key of editTag.constraints.exclusive) {
          tagEdits[key] = []
        }
      }
      onUpdate({ tagEdits })
    },
    [keyName, onUpdate, isUpdating, editTag],
  )

  return (
    <Menu>
      <BaseMenu.Trigger
        render={<div />}
        className={`flex flex-wrap gap-2 ${isUpdating ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
      >
        <ActionTagBadgeCollection keyName={keyName} tags={displayTags} editable />
      </BaseMenu.Trigger>
      <Menu.Content sideOffset={4} disableAnchorTracking className="w-56 p-0">
        <div className="flex items-center px-2 pt-1.5">
          <span className="text-foreground-secondary text-xs font-medium capitalize">{keyName}</span>
        </div>
        <Menu.RadioGroup value={currentValue} onValueChange={handleValueChange}>
          <div className="overflow-y-auto p-1" style={{ maxHeight: MAX_VISIBLE_HEIGHT }}>
            {editTag.options.map((opt) => (
              <Menu.RadioItem
                key={opt.value}
                value={opt.value}
                className="text-foreground-secondary data-[highlighted]:bg-element-hover data-[highlighted]:text-foreground flex cursor-default select-none items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-none"
              >
                <span className="flex flex-1 items-center gap-2 overflow-hidden">
                  <Icon src={opt.icon} className="size-4 shrink-0 rounded" />
                  <span className="truncate">{opt.name}</span>
                </span>
                <Menu.RadioItemIndicator className="flex shrink-0 items-center justify-center data-[unchecked]:invisible">
                  <Check className="size-3" />
                </Menu.RadioItemIndicator>
              </Menu.RadioItem>
            ))}
          </div>
        </Menu.RadioGroup>
      </Menu.Content>
    </Menu>
  )
}
